import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { MemberPreferences } from '@catalyst-psa/shared';

import { api } from '../../api/client.js';
import type { FavoriteItem, RecentItem } from './store.js';

export interface QuickAccessServerState {
  recents: RecentItem[];
  favorites: FavoriteItem[];
}

/** Same key the dashboards feature uses for the member's saved preferences. */
const preferencesKey = ['preferences'] as const;

/** The member's saved preferences; used here for the favorites-bar toggle. */
export function useMemberPreferences(): UseQueryResult<MemberPreferences> {
  return useQuery({
    queryKey: preferencesKey,
    queryFn: () => api.get<MemberPreferences>('/preferences'),
    staleTime: 60_000,
  });
}

/** Turns the top-of-screen favorites bar on or off. */
export function useSetShowFavoritesBar(): UseMutationResult<MemberPreferences, Error, boolean> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (show: boolean) =>
      api.put<MemberPreferences>('/preferences', { showFavoritesBar: show }),
    onSuccess: (data) => {
      queryClient.setQueryData(preferencesKey, data);
    },
  });
}

/** Loads the member's saved lists from the server; returns null on any failure. */
export async function fetchQuickAccess(): Promise<QuickAccessServerState | null> {
  try {
    const state = await api.get<{ recents?: RecentItem[]; favorites?: FavoriteItem[] }>(
      '/quick-access',
    );
    return { recents: state.recents ?? [], favorites: state.favorites ?? [] };
  } catch {
    return null;
  }
}

/**
 * Persists the member's full lists. Resolves true only when the server accepted the write,
 * so the caller can retry rather than assume the change was stored.
 *
 * `keepalive` lets an in-flight save outlive the page during an unload/tab close, which is
 * how a still-debounced change survives the user navigating away immediately after making it.
 */
export async function saveQuickAccess(
  state: QuickAccessServerState,
  options: { keepalive?: boolean } = {},
): Promise<boolean> {
  try {
    await api.put('/quick-access', state, options.keepalive ? { keepalive: true } : undefined);
    return true;
  } catch {
    return false;
  }
}
